import React, { useState, useEffect } from "react";
import { Alignment, Fit, Layout, useRive } from "@rive-app/react-canvas";
import "../styles/pages/MyStorybook.scss";
import { getMybookList } from "../api/LibraryAPI";
import loginSessionCheck from "../components/utils/LoginSessionCheck";
import BookCoverList from "../components/utils/BookCoverList";
import CategoryList from "../components/utils/CategoryList";
import { useNavigate } from "react-router-dom";
import AllViewModal from "../components/utils/modal/AllViewModal";
import Wrapper from "../components/utils/Wrapper";
import PaginationButtons from "../components/utils/button/PaginationButtons";
import ConfirmAudioBookModal from "../components/audiobook/ConfirmAudioBookModal";
import ListLoading from "../components/utils/loading/ListLoading";
import BookCoverSlideList from "../components/utils/BookCoverSlideList";
import { getLocalizedAssetPath } from "../utils/getLocalizedLogo";
import useAppTranslation from "../hooks/useAppTranslation";

const MyLibraryCharacterRive = ({ src }) => {
  const { RiveComponent } = useRive(
    {
      src,
      autoplay: true,
      layout: new Layout({
        fit: Fit.Contain,
        alignment: Alignment.Center,
      }),
    },
    {}
  );

  return <RiveComponent />;
};

const MyStorybook = () => {
  const { t, language } = useAppTranslation("pages/myStorybook");
  const navigate = useNavigate();
  const USER_ID = localStorage.getItem("userID");

  const [bookList, setBookList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [booksPerPage, setBooksPerPage] = useState(8);
  const categories = [
    { key: "all", label: t("categories.all") },
    { key: "novel", label: t("categories.novel") },
    { key: "picture", label: t("categories.picture") },
  ];

  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showAllModal, setShowAllModal] = useState(false);

  // 오디오북 만들기 프로세스
  const [isSelectMode, setIsSelectMode] = useState(false); // 오디오북 선택 모드
  const [selectedBookId, setSelectedBookId] = useState(null); // 오디오북 만들 book_id
  const [showAudioConfirmModal, setShowAudioConfirmModal] = useState(false);
  const [isAudioOnly, setIsAudioOnly] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, isAudioOnly]);

  useEffect(() => {
    if (showAllModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [showAllModal]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getMybookList(USER_ID);
        if (res.success) {
          console.log("@@ data =>", res.data);
          setBookList(res.data);
        } else {
          console.error("API 에러:", res.error.errMsg);
        }
      } catch (error) {
        console.error("API 호출 중 에러 발생:", error);
      }
    };
    fetchData();
  }, [USER_ID]);

  useEffect(() => {
    const updateBooksPerPage = () => {
      if (window.innerWidth < 1025) {
        setBooksPerPage(4);
      } else if (window.innerWidth < 1440) {
        setBooksPerPage(6);
      } else {
        setBooksPerPage(8);
      }
    };

    updateBooksPerPage();
    window.addEventListener("resize", updateBooksPerPage);
    return () => {
      window.removeEventListener("resize", updateBooksPerPage);
    };
  }, []);

  const handleClose = () => {
    setShowAllModal(false);
  };

  const handleConfirmAudioBook = () => {
    if (!selectedBookId) {
      alert(t("alerts.selectBook"));
      return;
    }

    const selectedBook = bookList.find(
      (book) => book.book_id === selectedBookId,
    );

    if (!selectedBook) {
      alert(t("alerts.bookNotFound"));
      return;
    }

    localStorage.setItem("book_id", selectedBook.book_id);

    navigate("/reading", {
      state: {
        bookList: selectedBook,
        pageName: "myStorybook",
        bookID: selectedBook.book_id,
        makeAudioBook: true,
      },
    });
  };

  console.log("selectedBookId", selectedBookId);

  const handleBookSelect = (book) => {
    console.log("선택된 책:", book);
    localStorage.setItem("book_id", book.book_id);

    if (book.has_audiobook) {
      navigate(`/audio-reading`, {
        state: {
          pageName: "myStorybook",
          bookID: book.book_id,
        },
      });
    } else {
      navigate("/reading", {
        state: {
          pageName: "myStorybook",
          bookID: book.book_id,
        },
      });
    }
  };

  const handleSelectForAudioBook = (book) => {
    setSelectedBookId(book.book_id);
    setShowAudioConfirmModal(true);
  };

  const [isManualScrolling, setIsManualScrolling] = useState(false);
  const scrollTimeoutRef = React.useRef(null);

  const sectionRefs = {
    all: React.useRef(null),
    novel: React.useRef(null),
    picture: React.useRef(null),
  };

  // Intersection Observer 설정
  useEffect(() => {
    if (isManualScrolling) return;

    const options = {
      root: document.querySelector(".book-slide-list-container"),
      rootMargin: "-10% 0px -70% 0px", // 화면 상단 부근에서 감지
      threshold: 0,
    };

    const callback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const category = entry.target.getAttribute("data-category");
          if (category) {
            setSelectedCategory(category);
          }
        }
      });
    };

    const observer = new IntersectionObserver(callback, options);

    // 각 섹션 관찰 시작
    Object.values(sectionRefs).forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => observer.disconnect();
  }, [isManualScrolling, bookList.length]); // 리스트 로드 후 다시 설정 필요할 수 있음

  const handleCategoryClick = (category) => {
    setIsManualScrolling(true);
    setSelectedCategory(category);

    let targetRef = null;
    if (category === "all") targetRef = sectionRefs.all;
    else if (category === "novel") targetRef = sectionRefs.novel;
    else if (category === "picture") targetRef = sectionRefs.picture;

    if (targetRef && targetRef.current) {
      targetRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    // 클릭 후 일정 시간 뒤에 다시 스크롤 감지 활성화
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      setIsManualScrolling(false);
    }, 1000); // 스무스 스크롤 완료 시간 고려
  };

  // 현재 활성 카테고리의 필터링된 도서 수 계산
  const getCurrentCategoryCount = () => {
    const filteredByAudio = bookList.filter(
      (b) => !isAudioOnly || b.has_audiobook,
    );

    if (selectedCategory === "all") return filteredByAudio.length;

    return filteredByAudio.filter((book) => {
      if (selectedCategory === "novel")
        return book.style === "그림 소설" || book.style === "graphic_novel";
      if (selectedCategory === "picture")
        return (
          book.style === "그림 동화" ||
          book.style === "picture_book" ||
          book.style === null
        );
      return false;
    }).length;
  };

  return (
    <Wrapper>
      <section className="mystorybook-section">
        <div className="inner">
          <div className="mystory-btn-container">
            {isSelectMode ? (
              <button
                type="button"
                className="audiobook-make-btn cancel"
                onClick={() => {
                  setIsSelectMode(false);
                }}
              >
                <span>{t("cancelAudio")}</span>
              </button>
            ) : (
              <button
                type="button"
                className="audiobook-make-btn"
                onClick={() => {
                  if (!isSelectMode) {
                    setIsSelectMode(true);
                    setSelectedBookId(null);
                  } else {
                    handleConfirmAudioBook();
                  }
                }}
              >
                <img
                  src={"/images/audiobook/audio-btn.svg"}
                  alt=""
                  loading="lazy"
                  decoding="async"
                />
                <span>{t("audioMake")}</span>
              </button>
            )}

            <button
              type="button"
              className="all-view-btn"
              onClick={() => {
                setShowAllModal(true);
              }}
            >
              <span>{t("allView")}</span>
              <span className="plus-icon">+</span>
            </button>
          </div>

          <h3 className="storybook-tit">
            <figure>
              <img
                src={getLocalizedAssetPath(
                  "/images/mystorybook/mystorybook-tit.svg",
                )}
                alt=""
                loading="lazy"
                decoding="async"
              />
            </figure>
          </h3>
          <div className="mybook-container">
            <div className="mybook-box left">
              <CategoryList
                categories={categories}
                selectedCategory={selectedCategory}
                onSelectCategory={handleCategoryClick}
              />
            </div>
            <div className="mybook-box right">
              <div className="box-title">
                <p>{t("summary", { count: getCurrentCategoryCount() })}</p>
                <div className="audio-toggle-container">
                  <span>{t("audioOnly")}</span>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={isAudioOnly}
                      onChange={(e) => setIsAudioOnly(e.target.checked)}
                    />
                    <span className="slider round"></span>
                  </label>
                </div>
              </div>

              <BookCoverSlideList
                books={bookList}
                isAudioOnly={isAudioOnly}
                categories={categories}
                onStorySelect={
                  isSelectMode ? handleSelectForAudioBook : handleBookSelect
                }
                isSelectMode={isSelectMode}
                selectedBookId={selectedBookId}
                sectionRefs={sectionRefs}
              />

              {loading && <ListLoading />}
            </div>
          </div>
        </div>
        {/* inner-end */}
        <figure className="navigate-char" aria-hidden="true">
          <MyLibraryCharacterRive
            key={language}
            src={getLocalizedAssetPath("/riv/myLibrary/mylibrary-arial.riv")}
          />
        </figure>
      </section>
      {showAllModal && (
        <AllViewModal
          bookList={bookList}
          onClose={handleClose}
          onStorySelect={handleBookSelect}
        />
      )}
      {showAudioConfirmModal && (
        <ConfirmAudioBookModal
          bookTitle={
            bookList.find((book) => book.book_id === selectedBookId)?.title ||
            ""
          }
          onConfirm={() => {
            handleConfirmAudioBook();
            setShowAudioConfirmModal(false);
            setIsSelectMode(false); // 선택모드 종료
          }}
          onCancel={() => {
            setShowAudioConfirmModal(false);
            setSelectedBookId(null); // 선택 초기화
          }}
        />
      )}
    </Wrapper>
  );
};

export default loginSessionCheck(MyStorybook);
